import { useTranslation } from 'react-i18next';
import {
  useState,
  forwardRef,
  useImperativeHandle,
  Ref,
  useCallback,
  useRef,
} from 'react';
import {
  Modal,
  addToast,
  showDialogInfo,
  SelectOptionType,
} from '@octano/global-ui';
import { Row, Col } from 'reactstrap';
import DynamicFormBase from '../../../../../components/DynamicForm/DynamicFormBase';
import Loading from '../../../../../components/info/Loading';
import { Report, ReportType } from '../../../../../types/Reports';
import { ReportSyncForm } from '../type';
import { getResourcesData } from '../api';
import {
  getBlobType,
  getFileName,
  getRawFileName,
  getRequiredOptionsFromStructure,
  parseFilenameData,
  parsePayload,
} from '../utils';
import { createReportDownloadHandler } from './ReportActionsCell/handlerCreator';
import { ExcelData } from '../../../../../types/excel';
import ExcelCreate from '../../../../../utils/excelCreate';
import { downloadFromBlob } from '../../../../../utils/blob';

export type DownloadReportModalMethods = {
  open: (row: Report) => void;
};
export type DownloadReportModalProps = { onGenerate?: () => void };

const FORMS: Partial<Record<ReportType, any>> = {
  AUDIT_OF_GRADES: require('../forms/PERIOD_ID__COURSE_ID__SECTION_ID.json'),
  LEARNING_RESULTS: require('../forms/PERIOD_ID__STUDY_PLAN_VERSION_ID.json'),
  SECTIONS_GRADES: require('../forms/PERIOD_ID.json'),
  STUDENT_SECTION_ENROLLED: require('../forms/PERIOD_ID.json'),
  STUDENT_PROGRESS: require('../forms/STUDY_PLAN_VERSION_ID.json'),
  STUDY_PLAN_ENROLLMENT_STATUS_HISTORY: require('../forms/STUDY_PLAN_ID.json'),
  ACTIVE_TEACHERS: require('../forms/PERIOD_ID.json'),
  ENROLLED_WITHOUT_TUITION: require('../forms/PERIOD_ASC__NAMED_FILE.json'),
  GRATUITY_LOSS: require('../forms/ACADEMIC_YEAR__NAMED_FILE.json'),
};

const HTTP_STATUS_NO_CONTENT = 204;
const DYNAMIC_FORM_SOURCE = 'reports';

const DownloadReportModal = (
  { onGenerate }: DownloadReportModalProps,
  ref: Ref<DownloadReportModalMethods>,
) => {
  const { t } = useTranslation();
  const fileName = useRef('');
  const closeOnError = useRef(false);

  const [loading, setLoading] = useState<boolean>(false);
  const [generating, setGenerating] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const [report, setReport] = useState<Report>();
  const [reportForm, setReportForm] = useState<ReportSyncForm>();
  const [reportOptions, setReportOptions] = useState<
    Record<string, SelectOptionType[]>
  >();

  const close = useCallback(() => {
    closeOnError.current = false;
    setIsOpen(false);
    setLoading(false);
    setGenerating(false);
    setReport(undefined);
    setReportForm(undefined);
    setReportOptions(undefined);
  }, []);

  const handleReportDownload = createReportDownloadHandler({
    beforeDownload: () => {
      setGenerating(true);
    },
    afterDownload: ({ status }) => {
      setLoading(false);
      setGenerating(false);
      if (status === HTTP_STATUS_NO_CONTENT) {
        showDialogInfo({
          title: t(`reports.empty.title`),
          subtitle: t(`reports.empty.subtitle`),
          btnConfirm: {
            text: t(`reports.empty.confirm`),
            onConfirm: () => {
              if (closeOnError.current) {
                close();
              }
            },
          },
        });
      }
    },
    onSuccess: (data: Blob | { data: ExcelData<any>[] }) => {
      if ('data' in data) {
        const excel = new ExcelCreate();
        excel.addManySheet(data.data);
        excel.exportToFile(getRawFileName(fileName.current));
        close();
        !!onGenerate && onGenerate();
      } else if (data.size > 0) {
        const blob = new Blob([data], { type: getBlobType(fileName.current) });
        downloadFromBlob(blob, fileName.current);
        close();
        !!onGenerate && onGenerate();
      } else if (closeOnError.current) {
        close();
      }
    },
    onError: () => {
      setGenerating(false);
      if (closeOnError.current) {
        close();
      }
      addToast({
        icon: 'error',
        color: 'danger',
        text: t(`reports.errors.errorGeneratingFile`),
      });
    },
  });

  const dispatchDownload = useCallback(
    async (row: Report, form?: ReportSyncForm, values?: any) => {
      fileName.current = getFileName(
        row.name,
        form?.filename,
        parseFilenameData(form?.structure || [], values),
      );
      closeOnError.current = !form?.structure?.length;
      await handleReportDownload(
        row.downloadUrl,
        parsePayload(form?.structure || [], values),
        row.renderSide,
      );
    },
    [handleReportDownload],
  );

  const handleGenerate = useCallback(
    async (values: any) => {
      if (generating || loading || !report) {
        return;
      }
      await dispatchDownload(report, reportForm, values);
    },
    [generating, reportForm, loading, dispatchDownload, report],
  );

  const fetchForm = useCallback(
    async (row: Report) => {
      if (generating || loading) {
        return;
      }
      setLoading(true);
      const nextForm = FORMS[row.type];
      if (!nextForm) {
        setReportForm(undefined);
        setLoading(false);
        return dispatchDownload(row);
      }
      setReportForm(nextForm);
      const { data, error } = await getResourcesData(
        getRequiredOptionsFromStructure(nextForm?.structure || []),
      );
      if (data && !error) {
        setReportOptions(data);
      } else {
        close();
      }
      setLoading(false);
    },
    [generating, loading, close, dispatchDownload],
  );

  const open = useCallback(
    (row: Report) => {
      setReport(row);
      fetchForm(row);
      setIsOpen(true);
      closeOnError.current = false;
    },
    [fetchForm],
  );

  useImperativeHandle(ref, () => ({ open }));

  return (
    <Modal isOpen={isOpen} toggle={() => !generating && close()} unmountOnClose>
      <Row className="text-center">
        <Col xs={12} className="pb-3">
          <span className="fs-22 text-dark">{report?.name || ''}</span>
        </Col>
        <Col xs={12}>
          <p className="fs-16 fw-300">
            {reportForm?.instructions || report?.description || ''}
          </p>
        </Col>
      </Row>
      {!!(reportForm?.structure?.length && !loading) && (
        <DynamicFormBase
          isModalRender
          source={DYNAMIC_FORM_SOURCE}
          options={reportOptions}
          structure={reportForm?.structure}
          onSubmit={handleGenerate}
          formButtons={[
            {
              type: 'button',
              text: t('common:actions.close'),
              onClick: close,
              disabledInLoading: true,
              disabledInSubmitting: true,
              outlined: true,
              fullwidth: true,
              col: { lg: 6 },
            },
            {
              type: 'submit',
              text: t('common:actions.download'),
              disabledInLoading: true,
              outlined: false,
              fullwidth: true,
              col: { lg: 6 },
            },
          ]}
        />
      )}
      {!!(loading || (generating && !reportForm?.structure?.length)) && (
        <Loading insideCard />
      )}
    </Modal>
  );
};

export default forwardRef(DownloadReportModal);
